import { useState, useEffect, useRef } from 'react'
import { ProcessingResult } from '../services/api'
import { apiClient } from '../services/api'
import { AlertCircle, CheckCircle, Clock, TrendingUp, Check, X, Edit2, Mail } from 'lucide-react'

interface ResultsPanelProps {
  results: ProcessingResult | null
  isLoading: boolean
}

export default function ResultsPanel({ results, isLoading }: ResultsPanelProps) {
  const [responseAction, setResponseAction] = useState<'pending' | 'accepted' | 'editing' | 'rejected'>('pending')
  const [editedResponse, setEditedResponse] = useState('')
  // Approval status polled from backend when email escalation is active
  const [approvalStatus, setApprovalStatus] = useState<'pending' | 'approved' | 'rejected'>('pending')
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Reset local state whenever a new result arrives
  useEffect(() => {
    setResponseAction('pending')
    setEditedResponse('')
    setApprovalStatus('pending')

    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }

    // Start polling only when the backend sent an escalation email
    if (results?.email_sent && results.approval_token) {
      const token = results.approval_token
      pollRef.current = setInterval(async () => {
        try {
          const { status } = await apiClient.pollApprovalStatus(token)
          if (status !== 'pending') {
            setApprovalStatus(status)
            if (pollRef.current) {
              clearInterval(pollRef.current)
              pollRef.current = null
            }
          }
        } catch {
          // silently ignore transient poll errors
        }
      }, 3000)
    }

    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [results])

  if (isLoading) {
    return (
      <div className="card flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Processing review...</p>
        </div>
      </div>
    )
  }

  if (!results) {
    return (
      <div className="card flex items-center justify-center h-96">
        <div className="text-center">
          <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Submit a review to see results</p>
        </div>
      </div>
    )
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'posted':
        return <CheckCircle className="w-5 h-5 text-success-600" />
      case 'escalated':
        return <AlertCircle className="w-5 h-5 text-warning-600" />
      default:
        return <Clock className="w-5 h-5 text-gray-600" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'posted':
        return 'badge-success'
      case 'escalated':
        return 'badge-warning'
      default:
        return 'badge-info'
    }
  }

  const handleEdit = () => {
    setEditedResponse(results.response)
    setResponseAction('editing')
  }

  const handleAccept = () => {
    setResponseAction('accepted')
  }

  const handleReject = () => {
    setResponseAction('rejected')
  }

  const handleSaveEdit = () => {
    setResponseAction('accepted')
  }

  const handleCancelEdit = () => {
    setResponseAction('pending')
    setEditedResponse('')
  }

  return (
    <div className="space-y-4">
      {/* Status */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Processing Result</h3>
          <div className={`badge ${getStatusColor(results.status)} flex items-center gap-2`}>
            {getStatusIcon(results.status)}
            {results.status.charAt(0).toUpperCase() + results.status.slice(1)}
          </div>
        </div>
      </div>

      {/* Intent Analysis */}
      <div className="card">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Intent Analysis</h3>
        <div className="space-y-3">
          <div>
            <p className="text-sm text-gray-600">Sentiment</p>
            <p className="text-lg font-semibold text-gray-900 capitalize">
              {results.intent.sentiment}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Urgency</p>
            <p className="text-lg font-semibold text-gray-900 capitalize">
              {results.intent.urgency}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-2">Topics</p>
            <div className="flex flex-wrap gap-2">
              {results.intent.topics.map((topic, idx) => (
                <span key={idx} className="badge badge-info">
                  {topic}
                </span>
              ))}
            </div>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-2">Key Points</p>
            <ul className="space-y-1">
              {results.intent.key_points.map((point, idx) => (
                <li key={idx} className="text-sm text-gray-700 flex gap-2">
                  <span className="text-primary-600">•</span>
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Generated Response */}
      <div className="card">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Generated Response</h3>

        {/* ── Escalation email path ── */}
        {results.email_sent ? (
          <div className="space-y-4">
            {/* Always show the generated response text */}
            <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">
              {results.response}
            </p>

            {/* Waiting for email action */}
            {approvalStatus === 'pending' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                <Mail className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-900">Approval email sent</p>
                  <p className="text-sm text-amber-700 mt-1">
                    This review was escalated because it is rated ≤ 2★ with negative sentiment.
                    An email with Approve / Reject options has been sent. Waiting for a response…
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <div className="w-3 h-3 rounded-full bg-amber-400 animate-pulse" />
                    <span className="text-xs text-amber-600">Polling for decision…</span>
                  </div>
                </div>
              </div>
            )}

            {/* Approved via email */}
            {approvalStatus === 'approved' && (
              <div className="bg-success-50 border border-success-200 rounded-lg p-4 flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-success-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-success-900">Response Approved</p>
                  <p className="text-sm text-success-700">
                    The response was approved via email and is ready to be posted.
                  </p>
                </div>
              </div>
            )}

            {/* Rejected via email */}
            {approvalStatus === 'rejected' && (
              <div className="bg-danger-50 border border-danger-200 rounded-lg p-4 flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-danger-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-danger-900">Response Rejected</p>
                  <p className="text-sm text-danger-700">
                    The response was rejected via email. No changes will be posted.
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* ── Normal (non-escalated) path — existing behaviour ── */
          <>
            {responseAction === 'pending' && (
              <>
                <p className="text-gray-700 bg-gray-50 p-4 rounded-lg mb-4">
                  {results.response}
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={handleAccept}
                    className="flex-1 btn btn-primary flex items-center justify-center gap-2"
                  >
                    <Check className="w-4 h-4" />
                    Accept
                  </button>
                  <button
                    onClick={handleEdit}
                    className="flex-1 btn btn-secondary flex items-center justify-center gap-2"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={handleReject}
                    className="flex-1 btn btn-outline-danger flex items-center justify-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Reject
                  </button>
                </div>
              </>
            )}

            {responseAction === 'editing' && (
              <>
                <textarea
                  value={editedResponse}
                  onChange={(e) => setEditedResponse(e.target.value)}
                  className="w-full p-4 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-primary-600"
                  rows={4}
                />
                <div className="flex gap-3">
                  <button
                    onClick={handleSaveEdit}
                    className="flex-1 btn btn-primary flex items-center justify-center gap-2"
                  >
                    <Check className="w-4 h-4" />
                    Save & Accept
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="flex-1 btn btn-outline-secondary flex items-center justify-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                </div>
              </>
            )}

            {responseAction === 'accepted' && (
              <div className="bg-success-50 border border-success-200 rounded-lg p-4 flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-success-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-success-900">Response Accepted</p>
                  <p className="text-sm text-success-700">Thank you! The response is ready to be sent.</p>
                </div>
              </div>
            )}

            {responseAction === 'rejected' && (
              <div className="bg-danger-50 border border-danger-200 rounded-lg p-4 flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-danger-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-danger-900">Response Rejected</p>
                  <p className="text-sm text-danger-700">No changes were made. You can submit a new review to generate another response.</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Validation */}
      <div className="card">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Validation</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">Quality Score</p>
            <div className="flex items-center gap-2">
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all"
                  style={{ width: `${results.validation.score}%` }}
                ></div>
              </div>
              <span className="font-bold text-gray-900">
                {results.validation.score}/100
              </span>
            </div>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-2">Status</p>
            <p className={`text-sm font-medium ${
              results.validation.is_valid ? 'text-success-600' : 'text-danger-600'
            }`}>
              {results.validation.is_valid ? '✓ Valid' : '✗ Invalid'}
            </p>
          </div>
          {results.validation.issues && results.validation.issues.length > 0 && (
            <div>
              <p className="text-sm text-gray-600 mb-2">Issues</p>
              <ul className="space-y-1">
                {results.validation.issues.map((issue, idx) => (
                  <li key={idx} className="text-sm text-danger-600 flex gap-2">
                    <span>•</span>
                    {issue}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Escalation */}
      <div className="card">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Escalation</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">Should Escalate</p>
            <p className={`text-sm font-bold ${
              results.escalation.should_escalate ? 'text-warning-600' : 'text-success-600'
            }`}>
              {results.escalation.should_escalate ? 'Yes' : 'No'}
            </p>
          </div>
          {results.escalation.should_escalate && (
            <>
              <div>
                <p className="text-sm text-gray-600 mb-2">Priority</p>
                <p className={`text-sm font-bold capitalize ${
                  results.escalation.priority === 'high' ? 'text-danger-600' :
                  results.escalation.priority === 'medium' ? 'text-warning-600' :
                  'text-primary-600'
                }`}>
                  {results.escalation.priority}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-2">Reasons</p>
                <ul className="space-y-1">
                  {results.escalation.reasons.map((reason, idx) => (
                    <li key={idx} className="text-sm text-gray-700 flex gap-2">
                      <span className="text-warning-600">•</span>
                      {reason}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
